import { InlineError } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, type SsoAuditEntry } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { ssoChangeActor, ssoChangeSummary, ssoChangeTarget } from "@/lib/sso-audit";
import { cn } from "@/lib/utils";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 20;
// Shown before "Show all".
const PREVIEW = 3;

// Who changed sign-in for this Boo's apps, newest first: people in the
// dashboard and the Boo itself. Hidden until there is a change to show.
export function SignInChanges({ containerId }: { containerId: string }) {
  const settingsQuery = useQuery({
    queryKey: ["sso-settings", containerId],
    queryFn: () => api.sso.settings(containerId),
  });
  const enabled = settingsQuery.data?.enabled === true;
  const [showAll, setShowAll] = useState(false);
  const audit = useInfiniteQuery({
    queryKey: ["sso-audit", containerId],
    queryFn: ({ pageParam }) => api.sso.audit(containerId, { limit: PAGE_SIZE, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled,
  });

  if (!enabled) return null;
  if (audit.isError) {
    return (
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Sign-in changes</h2>
        <InlineError error={audit.error} />
      </section>
    );
  }
  const entries = audit.data?.pages.flatMap((p) => p.data) ?? [];
  if (!entries.length) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-balance">Sign-in changes</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          Every change to who can open this Boo's apps and how people sign in, by your team or by the Boo.
        </p>
      </div>
      <ul className="overflow-hidden rounded-xl border divide-y">
        {(showAll ? entries : entries.slice(0, PREVIEW)).map((entry) => (
          <li key={entry.id}>
            <ChangeRow entry={entry} />
          </li>
        ))}
      </ul>
      {!showAll && (entries.length > PREVIEW || audit.hasNextPage) && (
        <Button size="sm" variant="ghost" className="self-start" onClick={() => setShowAll(true)}>
          Show all
        </Button>
      )}
      {showAll && audit.hasNextPage && (
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => audit.fetchNextPage()}
          disabled={audit.isFetchingNextPage}
        >
          {audit.isFetchingNextPage && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Show older changes
        </Button>
      )}
    </section>
  );
}

function ChangeRow({ entry }: { entry: SsoAuditEntry }) {
  const byBoo = entry.actorType === "agent";
  // Opening an app to anyone who signs in is the change worth noticing.
  const opened = entry.event === "policy_updated" && entry.after?.mode === "open" && entry.before?.mode !== "open";
  return (
    <div className="flex flex-col gap-1 px-4 py-2.5">
      <div className="text-sm text-pretty">
        {/* A new app's line already names it. */}
        {entry.event !== "client_registered" && (
          <>
            <span className="font-medium">{ssoChangeTarget(entry)}</span>
            <span className="text-muted-foreground"> · </span>
          </>
        )}
        <span className={cn(opened ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground")}>
          {ssoChangeSummary(entry)}
        </span>
      </div>
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        {byBoo ? (
          <Badge variant="secondary" className="h-5 px-1.5">
            Boo
          </Badge>
        ) : (
          <span className="truncate" title={entry.actorEmail ?? undefined}>
            {ssoChangeActor(entry)}
          </span>
        )}
        <span aria-hidden>·</span>
        <time dateTime={entry.createdAt} title={new Date(entry.createdAt).toLocaleString()} className="shrink-0 tabular-nums">
          {relativeIso(entry.createdAt)}
        </time>
      </div>
    </div>
  );
}
